import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save, 
  X, 
  Plus, 
  Minus, 
  Clock, 
  Calendar,
  User,
  Building2,
  Package,
  FileText,
  AlertTriangle,
  Printer,
  QrCode
} from 'lucide-react';
import QRCode from 'qrcode';

const BatchForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    product: '',
    location: '',
    shift: '',
    quantity: '',
    chef: '',
    team: [''],
    notes: '',
    temperature: '',
    humidity: '',
    batchNumber: '',
    qualityChecks: {
      temperature: { value: '', unit: '°C', status: 'pending' },
      moisture: { value: '', unit: '%', status: 'pending' },
      color: { value: '', unit: '', status: 'pending' }
    },
    expiryDate: '',
    productionDate: new Date().toISOString().split('T')[0],
    productionTime: new Date().toTimeString().split(' ')[0].slice(0, 5),
    ingredients: [{ name: '', quantity: '', unit: '' }]
  });

  const [generatedBatchNumber, setGeneratedBatchNumber] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  const locations = [
    'Central Kitchen 1',
    'Central Kitchen 2',
    'Production Unit A'
  ];

  const shifts = ['Morning', 'Afternoon', 'Night'];
  const products = ['Sauce Base', 'Marinade Mix', 'Spice Blend', 'Dough Mix', 'Dessert Base'];
  const chefs = ['Ahmed Ali', 'Sara Khan', 'Mohammed Hassan', 'Fatima Abbas'];
  const units = ['kg', 'g', 'L', 'ml', 'units'];

  const generateBatchNumber = () => {
    if (!formData.location || !formData.shift) {
      alert('Please select location and shift first');
      return;
    }

    const prefix = formData.location.substring(0, 2).toUpperCase();
    const shift = formData.shift.charAt(0).toUpperCase();
    const date = new Date().toISOString().slice(2,4); // Year
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    const batchNumber = `${prefix}${shift}${date}${random}${sequence}`;
    setGeneratedBatchNumber(batchNumber);
    
    setFormData(prev => ({
      ...prev,
      batchNumber: batchNumber
    }));

    // Generate QR code
    generateQRCode(batchNumber);
  };

  const generateQRCode = async (batchNumber) => {
    try {
      const url = await QRCode.toDataURL(batchNumber);
      setQrCodeUrl(url);
    } catch (err) {
      console.error('Error generating QR code:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTeamMemberChange = (index, value) => {
    const newTeam = [...formData.team];
    newTeam[index] = value;
    setFormData(prev => ({
      ...prev,
      team: newTeam
    }));
  };

  const handleQualityCheckChange = (parameter, value) => {
    setFormData(prev => ({
      ...prev,
      qualityChecks: {
        ...prev.qualityChecks,
        [parameter]: {
          ...prev.qualityChecks[parameter],
          value,
          status: value ? 'pass' : 'pending'
        }
      }
    }));
  };

  const addTeamMember = () => {
    setFormData(prev => ({
      ...prev,
      team: [...prev.team, '']
    }));
  };

  const removeTeamMember = (index) => {
    setFormData(prev => ({
      ...prev,
      team: prev.team.filter((_, i) => i !== index)
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = {
      ...newIngredients[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      ingredients: newIngredients
    }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', quantity: '', unit: '' }]
    }));
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handlePrintLabel = async () => {
    if (!generatedBatchNumber) {
      alert('Please generate a batch number first');
      return;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Batch Label - ${generatedBatchNumber}</title>
          <style>
            @page { size: 3in 2in; margin: 0; }
            body { 
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 10px;
              width: 3in;
              height: 2in;
            }
            .label {
              padding: 10px;
              text-align: center;
            }
            .batch-info {
              font-size: 10px;
              margin-top: 10px;
              text-align: left;
            }
            .batch-number {
              font-size: 14px;
              font-weight: bold;
              margin: 5px 0;
            }
            .qr-code {
              width: 100px;
              height: 100px;
              margin: 5px auto;
            }
            @media print {
              body { margin: 0; padding: 10px; }
            }
          </style>
        </head>
        <body>
          <div class="label">
            <div style="font-weight: bold; font-size: 12px;">${formData.product}</div>
            <img src="${qrCodeUrl}" class="qr-code"/>
            <div class="batch-number">${generatedBatchNumber}</div>
            <div class="batch-info">
              <div>Production: ${formData.productionDate}</div>
              <div>Expiry: ${formData.expiryDate}</div>
              <div>Location: ${formData.location}</div>
              <div>Quantity: ${formData.quantity} units</div>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!generatedBatchNumber) {
      alert('Please generate a batch number before saving');
      return;
    }
    console.log('Form data:', formData);
    navigate('/batch-control');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Create New Batch</h1>
            <p className="text-gray-500 mt-2">Record new production batch details</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/batch-control')}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Batch Number Generation */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h2 className="text-xl font-semibold mb-6">Batch Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Batch Number
                </label>
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={generatedBatchNumber}
                    readOnly
                    className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Generated batch number will appear here"
                  />
                  <button
                    type="button"
                    onClick={generateBatchNumber}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                  >
                    Generate Number
                  </button>
                </div>
              </div>

              {generatedBatchNumber && (
                <div className="flex items-center gap-4">
                  {qrCodeUrl && (
                    <div className="bg-white p-4 rounded-lg">
                      <img src={qrCodeUrl} alt="QR Code" className="w-24 h-24" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handlePrintLabel}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print Label
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product
                </label>
                <select
                  name="product"
                  value={formData.product}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Product</option>
                  {products.map(product => (
                    <option key={product} value={product}>{product}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Location</option>
                  {locations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shift
                </label>
                <select
                  name="shift"
                  value={formData.shift}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Shift</option>
                  {shifts.map(shift => (
                    <option key={shift} value={shift}>{shift}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter quantity"
                />
              </div>
            </div>
          </div>

          {/* Production Team */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h2 className="text-xl font-semibold mb-6">Production Team</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chef in Charge
                </label>
                <select
                  name="chef"
                  value={formData.chef}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Chef</option>
                  {chefs.map(chef => (
                    <option key={chef} value={chef}>{chef}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Members
                </label>
                {formData.team.map((member, index) => (
                  <div key={index} className="flex gap-4 mb-4">
                    <select
                      value={member}
                      onChange={(e) => handleTeamMemberChange(index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Team Member</option>
                      {chefs.map(chef => (
                        <option key={chef} value={chef}>{chef}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeTeamMember(index)}
                      className="p-2 text-gray-500 hover:text-red-500"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addTeamMember}
                  className="flex items-center text-indigo-600 hover:text-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Team Member
                </button>
              </div>
            </div>
          </div>

          {/* Quality Checks */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h2 className="text-xl font-semibold mb-6">Quality Checks</h2>
            <div className="space-y-6">
              {/* Temperature Check */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temperature
                </label>
                <div className="flex gap-4">
                  <input
                    type="number"
                    value={formData.qualityChecks.temperature.value}
                    onChange={(e) => handleQualityCheckChange('temperature', e.target.value)}
                    className="flex-1 px-4 py-2 border rounded-lg"
                    placeholder="Enter temperature"
                  />
                  <span className="flex items-center text-gray-500">°C</span>
                </div>
              </div>

              {/* Moisture Check */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moisture Content
                </label>
                <div className="flex gap-4">
                  <input
                    type="number"
                    value={formData.qualityChecks.moisture.value}
                    onChange={(e) => handleQualityCheckChange('moisture', e.target.value)}
                    className="flex-1 px-4 py-2 border rounded-lg"
                    placeholder="Enter moisture percentage"
                  />
                  <span className="flex items-center text-gray-500">%</span>
                </div>
              </div>

              {/* Color Check */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <select
                  value={formData.qualityChecks.color.value}
                  onChange={(e) => handleQualityCheckChange('color', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="">Select color standard</option>
                  <option value="Standard">Standard</option>
                  <option value="Light">Light</option>
                  <option value="Dark">Dark</option>
                  <option value="Off-spec">Off-spec</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h2 className="text-xl font-semibold mb-6">Additional Information</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter any additional notes or observations"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Production Date
                  </label>
                  <input
                    type="date"
                    name="productionDate"
                    value={formData.productionDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Production Time
                  </label>
                  <input
                    type="time"
                    name="productionTime"
                    value={formData.productionTime}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/batch-control')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <Save className="w-5 h-5" />
              Save Batch
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchForm;